import React, {Component, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { navigate } from '../../../helpers/navigationService';
import {
  getNumberOfMonths,
  seriesHasSchedule,
  getCurrentMonth,
  hasActiveInventoryFilter,
  getSeriesOption,
  getFormattedEventDates
} from '../selectors/eventSeries';
import SeriesDayPicker from '../components/SeriesDayPicker';
import { setEventTimes, setSelectedDate, handleMonthChange, lazyLoadSeriesDates } from '../actions/event-series';
import { removeListingItem } from '../../../../listing/helpers/storage';
import { setCheckoutOverlay, setShowCalendarInModal } from '../../../actions/ui';
import { deleteReservation } from '../../../actions/reservation';
import { isValidDateObject } from '../../../../helpers/time';
import { reservationActive } from '../../../selectors/reservation';
import { createLoadingSelector } from 'checkout/selectors/helpers';
import { getMainEvent } from 'checkout/selectors/event';
import InventoryFilterContainer from './InventoryFilterContainer';
import { EMBED_TYPE_CALENDAR } from '../../../reducers/ui';
import Translation from '@s/Translation';
import { getTimestampAndRemoveFromUrl } from "../../../helpers/url";
import {isSameDay} from "../../../../../../shared/util/dates";
import { CalendarLegends } from '../components/CalendarLegends';

const DEFAULT_SOLD_OUT_TEXT = 'SOLD OUT';
const ListingDatePicker = ({
    soldOutMessage,
    outerClassName,
    inventoryFilter,
    isEmbed,
    selectDate,
    isTicketsOnSale,
    showSelectDateLabel,
    hasSchedule,
    ...dayPickerProps
}) => {
  useEffect(() => {
    const timestamp = getTimestampAndRemoveFromUrl('date');

    if (timestamp) {
      selectDate(new Date(timestamp));
    }
  }, [])

  if (!isTicketsOnSale) {
    return null;
  }

  return soldOutMessage ? (<span>{soldOutMessage}</span>) : (
    <dialog
      className={outerClassName}
    >
      {inventoryFilter && (
        <div id={isEmbed ? "checkout-overlay" : ""} className={isEmbed ? "embed__inventory-filter" : ""}>
          <div className="card">
            <h2 className="tickets__inventory-title">
              <Translation>{'checkout__find_tickets'}</Translation>
            </h2>
            <InventoryFilterContainer/>
          </div>
        </div>
      )}
      <div className={`DayPicker--listing tickets__datepicker ${isEmbed ? "embed__listing-date-picker" : ""}`}>
        {(showSelectDateLabel && hasSchedule) && (
          <div className="picker__header">
            <i className='icon-calendar' aria-label='calendar' />
            <h4 className="picker__header-title">
              <Translation>{'checkout__select_date'}</Translation>
            </h4>
          </div>
        )}
        <CalendarLegends />
        <SeriesDayPicker {...dayPickerProps} />
      </div>
    </dialog>
  )
}

const eventTimesLoading = createLoadingSelector("EVENT_TIMES");
const mapStateToProps = (state) => {
  if (state.ui.listingSoldOut) {
    return {
      soldOutMessage:
        (state.ui.uiConfig?.listings?.useCustomSoldOutText &&
          typeof state.ui.uiConfig.listings.soldOutText !== "undefined" &&
          state.ui.uiConfig.listings.soldOutText !== "")
        ? state.ui.uiConfig.listings.soldOutText
        : DEFAULT_SOLD_OUT_TEXT
    }
  }

  const theEvent = getMainEvent(state)?.event ?? {};
  const hasSchedule = seriesHasSchedule(state);

  return {
    listingSlug: state.ui.listingSlug,
    numberOfMonths: hasSchedule ? getNumberOfMonths(state) : 1,
    eventTimesLoading: eventTimesLoading(state),
    selectedDate: state.eventSeries.selectedDate,
    reservationActive: reservationActive(state),
    listingSoldOut: state.ui.listingSoldOut,
    isMemberLoggedIn: state.ui.hasMembershipContext,
    uiConfig: state.ui.uiConfig,
    month: getCurrentMonth(state),
    loadedDates: state.eventSeries.datesLazyLoaded,
    hasActiveInventoryFilter: hasActiveInventoryFilter(state),
    selectedDateObject: state.eventSeries.selectedDateObject,
    eventDates: getFormattedEventDates(state),
    inventoryFilter: state.ui.embedType == EMBED_TYPE_CALENDAR &&
                      getSeriesOption('inventoryFilter')(state) &&
                      getSeriesOption("calendarType")(state) !== 'list',
    isEmbed: state.ui.embedType == EMBED_TYPE_CALENDAR,
    isOnsale: theEvent.isOnsale ?? false,
    isCanceled: theEvent.isCanceled,
    isPaused: theEvent.isPaused,
    inviteCouponApplied: state.couponTag.appliedTaggableInvitationCode || state.reservation.hasInviteCoupon,
    hasPresale: theEvent.hasPresale,
    hasSchedule: hasSchedule
  };
}

const mapDispatchToProps = dispatch =>
    bindActionCreators({
        setCheckoutOverlay,
        setEventTimes,
        setSelectedDate,
        lazyLoadSeriesDates,
        setShowCalendarInModal,
        deleteReservation,
        handleMonthChange,
    }, dispatch);

const mergeProps = (stateProps, dispatchProps, ownProps) => {
    if (stateProps.soldOutMessage) {
        return { soldOutMessage: stateProps.soldOutMessage };
    }

    const getSelectedEventTimes = day => {
        dispatchProps.setSelectedDate(day).then(() => {
          dispatchProps.setCheckoutOverlay(true);
          navigate('/day');
        });
    };

    const isTicketsOnSale = !stateProps.isCanceled && !stateProps.isPaused && (stateProps.invitationCouponApplied || stateProps.isOnsale || (!stateProps.isOnsale && stateProps.hasPresale));

    return {
        outerClassName:
            stateProps.numberOfMonths === 2
                ? "tickets-wrap__calendar-double"
                : "tickets-wrap__calendar-single",
        selectedDay: stateProps.selectedDate,
        onMonthChange: dispatchProps.handleMonthChange,
        inventoryFilter: stateProps.inventoryFilter,
        isEmbed: stateProps.isEmbed,
        isTicketsOnSale,
        showSelectDateLabel: ownProps.showSelectDateLabel,
        hasSchedule: stateProps.hasSchedule,
        selectDate: async (date) => {
          const isDateIsAvailable = stateProps.eventDates.some((availableDate) => isSameDay(availableDate, date))

          if(!isDateIsAvailable) {
              return false;
          }

          dispatchProps.setEventTimes([]);
          dispatchProps.setShowCalendarInModal(false);

          getSelectedEventTimes(date);
        },
        onDayClick: async (day, modifiers = {}) => {
            if(stateProps.eventTimesLoading || modifiers.disabled) {
                return false;
            }
            dispatchProps.setEventTimes([]);
            removeListingItem(stateProps.listingSlug, 'selectedEventId');
            dispatchProps.setShowCalendarInModal(false);

            //if user already selected a different date, and there is an active reservation, delete it here
            const pickingSameDay = isValidDateObject(day)
                && isValidDateObject(stateProps.selectedDateObject)
                && day.getTime() === stateProps.selectedDateObject.getTime();
            if (stateProps.reservationActive && (!isValidDateObject(stateProps.selectedDateObject) || !pickingSameDay) && !stateProps.isMemberLoggedIn) {
                await dispatchProps.deleteReservation(false);
            }
            getSelectedEventTimes(day);
        },
    };
}

ListingDatePicker.defaultProps = {
  showSelectDateLabel: false
};

ListingDatePicker.propTypes = {
};
export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(ListingDatePicker)
